/**
 * Product Tabs Web Component
 * Handles tab switching, keyboard navigation, hash support,
 * and lazy re-init of app widgets (e.g. Judge.me).
 */
class ProductTabs extends HTMLElement {
  #controller = new AbortController();

  /** @type {NodeListOf<HTMLButtonElement>} */
  get tabs() {
    return this.querySelectorAll('[role="tab"]');
  }

  /** @type {NodeListOf<HTMLElement>} */
  get panels() {
    return this.querySelectorAll('[role="tabpanel"]');
  }

  get firstTab() {
    return this.dataset.firstTab || '';
  }

  connectedCallback() {
    const { signal } = this.#controller;

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', this.#handleTabClick, { signal });
    });

    this.addEventListener('keydown', this.#handleKeyDown, { signal });

    // Build hash → tabId map from data-hash attributes
    this.#hashMap = {};
    this.tabs.forEach((tab) => {
      const panel = this.querySelector(`[data-panel="${tab.dataset.tab}"]`);
      if (panel) {
        const hash = panel.dataset.hash;
        if (hash) this.#hashMap[hash.toLowerCase()] = tab.dataset.tab;
      }
    });

    this.#handleHashChange();
    window.addEventListener('hashchange', this.#handleHashChange, { signal });
    window.openProductTab = this.openTab.bind(this);

    // Intercept Judge.me preview badge clicks to open reviews tab first
    this.#setupJudgeMeBadgeIntercept(signal);

    // Init app widgets in the first visible tab
    this.#initAppWidgets(this.firstTab);
  }

  disconnectedCallback() {
    this.#controller.abort();
    delete window.openProductTab;
  }

  /** @type {Record<string, string>} hash → tabId */
  #hashMap = {};

  #handleTabClick = (event) => {
    this.openTab(event.currentTarget.dataset.tab, false);
  };

  #handleKeyDown = (event) => {
    const target = event.target;
    if (target.getAttribute('role') !== 'tab') return;

    const tabsArray = Array.from(this.tabs);
    const currentIndex = tabsArray.indexOf(target);
    let newIndex;

    switch (event.key) {
      case 'ArrowLeft':
        newIndex = currentIndex - 1;
        if (newIndex < 0) newIndex = tabsArray.length - 1;
        event.preventDefault();
        break;
      case 'ArrowRight':
        newIndex = currentIndex + 1;
        if (newIndex >= tabsArray.length) newIndex = 0;
        event.preventDefault();
        break;
      case 'Home':
        newIndex = 0;
        event.preventDefault();
        break;
      case 'End':
        newIndex = tabsArray.length - 1;
        event.preventDefault();
        break;
      default:
        return;
    }

    const newTab = tabsArray[newIndex];
    newTab.focus();
    this.openTab(newTab.dataset.tab, false);
  };

  #handleHashChange = () => {
    const hash = window.location.hash.slice(1).toLowerCase();
    if (!hash) return;

    const tabId = this.#hashMap[hash];
    if (tabId && this.#hasTab(tabId)) {
      this.openTab(tabId, true);
    }
  };

  #hasTab(tabId) {
    return !!this.querySelector(`[data-tab="${tabId}"]`);
  }

  /**
   * Open a specific tab
   * @param {string} tabId
   * @param {boolean} scroll
   */
  openTab(tabId, scroll = false) {
    if (!this.#hasTab(tabId)) return;

    this.tabs.forEach((tab) => {
      const isActive = tab.dataset.tab === tabId;
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    });

    this.panels.forEach((panel) => {
      const isActive = panel.dataset.panel === tabId;
      panel.hidden = !isActive;
    });

    this.#initAppWidgets(tabId);

    // Update URL hash
    const panel = this.querySelector(`[data-panel="${tabId}"]`);
    const hashValue = panel?.dataset.hash;
    if (hashValue && window.location.hash !== `#${hashValue}`) {
      history.replaceState(null, '', `#${hashValue}`);
    }

    if (scroll) {
      this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  /**
   * Intercept clicks on Judge.me preview badge (star rating at top of product).
   * Opens the reviews tab and scrolls there.
   */
  #setupJudgeMeBadgeIntercept(signal) {
    // Find reviews tab at init — Shopify server-renders app blocks,
    // so #judgeme_product_reviews exists in static HTML.
    this.#reviewsTabId = null;
    for (const panel of this.panels) {
      if (panel.querySelector('[id*="judgeme"], [class*="jdgm"]')) {
        this.#reviewsTabId = panel.dataset.panel;
        break;
      }
    }
    if (!this.#reviewsTabId) return;

    document.addEventListener(
      'click',
      (e) => {
        if (!e.target.closest('.jdgm-prev-badge')) return;

        e.preventDefault();
        e.stopPropagation();

        this.openTab(this.#reviewsTabId, false);
        // Delay scroll — #initAppWidgets does a micro-scroll that conflicts
        setTimeout(() => {
          this.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 150);
      },
      { signal, capture: true }
    );
  }

  #reviewsTabId = null;

  /**
   * Re-initialize app widgets (e.g. Judge.me) in a panel after it becomes visible.
   * @param {string} tabId
   */
  #initAppWidgets(tabId) {
    requestAnimationFrame(() => {
      window.dispatchEvent(new Event('resize'));
      // Nudge IntersectionObserver so Judge.me detects the now-visible widget
      window.scrollBy(0, 1);
      requestAnimationFrame(() => window.scrollBy(0, -1));
    });
  }
}

if (!customElements.get('product-tabs')) {
  customElements.define('product-tabs', ProductTabs);
}
